// Extracted from original/src/App.tsx (ADR-08 §2.1), the `activeTab === "dashboard"` block:
//   quick-action row   :26404-26645 (withdraw, deposit, manual work, cash transfers, store sell)
//   stat-card grid     :26646-28100 (totalOwed/totalCredit/monthlyExpenses :26840-26945, totalProfit :26946-27420,
//                       inventory :27421-27455, installments :27456-27500, count :27503-27625, vault :27626-27660,
//                       totalDigital :27661-27700, dollarVault :27742-27860, currenciesVault :27861-27960, capital :27961-28000)
// Cut: every handler and `dashboardData` calculation (figures arrive formatted from the container / B4 preview
// endpoint, ADR-08 §2.1 (2)); the permission filter `allowedDashboardOrder` (:2630-2666, container); the
// dashboard-editor overlays of the four custom cards (remove / resize / move, :26790-26840 etc.) — StatCard keeps
// its own editing props; `arnoubBox` and the two `operatorSubSafe*` cards (hidden by default, B4); TradingChart
// (recharts) → `chart` slot.
// [C26] The profit card's USD and other-currency lines (:27116-27409), cut in C18 while no profit figure existed, are
// restored verbatim; the latest profit withdrawal line is new (D-099, V-50).
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import {
  Activity, ArrowDownLeft, ArrowUpDown, ArrowUpRight, CalendarClock, Coins, Database, DollarSign, Package,
  ShieldCheck, ShoppingBag, TrendingDown, TrendingUp, Wallet, Zap,
} from 'lucide-react';
import { DynamicIcon, getIconContainerSizeClass, getLucideIconSize } from '../components/HelperComponents.js';
import { StatCard } from '../components/StatCard.js';
import { containerVariants, itemVariants } from '../constants.js';
import { useI18n } from '../i18n-context.js';

export type DashboardIconSize = 'sm' | 'md' | 'lg' | 'xl';
export type DashboardCardSize = 'sm' | 'md' | 'lg';

interface CardBase {
  readonly size?: DashboardCardSize;
  readonly colSpan?: 1 | 2 | 3 | 4;
  /** Per-user icon override (D-124 icon picker); a name of ICON_COMPONENTS. */
  readonly customIcon?: string;
  readonly onClick?: () => void;
}

/** A figure in a currency other than IQD / USD (formatted by the container). */
export interface DashboardCurrencyFigure {
  readonly currency: string;
  readonly value: string;
}

/** Every figure is a string formatted by the container (ADR-02 §2.9: no money arithmetic in the UI). */
export type DashboardCard =
  | (CardBase & { readonly kind: 'chart'; readonly slot: ReactNode })
  | (CardBase & { readonly kind: 'totalOwed'; readonly value: string; readonly usdValue?: string; readonly lastMovement: string })
  | (CardBase & { readonly kind: 'totalCredit'; readonly value: string; readonly usdValue?: string; readonly lastMovement: string })
  | (CardBase & { readonly kind: 'monthlyExpenses'; readonly value: string; readonly totalExpenses: string })
  | (CardBase & {
      readonly kind: 'totalProfit';
      readonly pendingProfit: string;
      /** Given only when > 0 and USD dealing is shown (old `showExchangeRate && … > 0`, App.tsx:27116-27118). */
      readonly pendingProfitUsd?: string;
      /** Currencies other than IQD / USD with a figure (old `currencyProfits`, App.tsx:27133-27169). */
      readonly pendingOther?: readonly DashboardCurrencyFigure[];
      readonly netDailyProfit: string;
      readonly netDailyProfitUsd?: string;
      readonly dailyOther?: readonly DashboardCurrencyFigure[];
      readonly grossDailyProfit: string;
      readonly grossDailyProfitUsd?: string;
      /** [C26] D-099: the latest capitalization (old: the session value, always 0 after a settlement, F-142). */
      readonly capitalizedProfit: string;
      readonly capitalizedProfitUsd?: string;
      /** [C26] D-099: the latest profit withdrawal, shown apart; the line renders only when one exists (V-50). */
      readonly lastWithdrawnProfit?: string;
      readonly lastWithdrawnProfitUsd?: string;
      readonly netMonthlyProfit: string;
      readonly netMonthlyProfitUsd?: string;
      readonly monthlyOther?: readonly DashboardCurrencyFigure[];
      readonly grossMonthlyProfit: string;
      readonly grossMonthlyProfitUsd?: string;
      /** "إجمالي الأرباح" = net since the last settlement (D06 R-21, D-099). */
      readonly totalProfit: string;
      readonly totalProfitUsd?: string;
    })
  | (CardBase & { readonly kind: 'inventory'; readonly value: string })
  | (CardBase & { readonly kind: 'installments'; readonly value: string; readonly usdValue?: string; readonly activeContracts: string })
  /** Counters are integers as strings; the card pads them to three digits like the old app (App.tsx:27595, 27606, 27616). */
  | (CardBase & { readonly kind: 'count'; readonly monthly: string; readonly weekly: string; readonly daily: string })
  | (CardBase & { readonly kind: 'vault'; readonly value: string })
  | (CardBase & { readonly kind: 'totalDigital'; readonly value: string; readonly inclCommission: string })
  | (CardBase & { readonly kind: 'dollarVault'; readonly value: string })
  | (CardBase & { readonly kind: 'currenciesVault'; readonly value: string })
  /** No add / withdraw / capitalise buttons: the old dashboard card never passed them (App.tsx:27999-28024). */
  | (CardBase & { readonly kind: 'capital'; readonly value: string; readonly initialCapital: string });

export interface DashboardQuickActions {
  readonly onWithdraw?: () => void;
  readonly onDeposit?: () => void;
  readonly onManualWork?: () => void;
  /** `undefined` hides the card (showCashTransfers === false, :26571). */
  readonly onTransfersCash?: () => void;
  /** `undefined` hides the card (showStore === false or no `store` permission, :26609). */
  readonly onStoreSell?: () => void;
}

export interface DashboardProps {
  readonly iconSize: DashboardIconSize;
  readonly cardSize: DashboardCardSize;
  readonly quickActions: DashboardQuickActions;
  readonly cards: readonly DashboardCard[];
  readonly isEditing?: boolean;
  readonly refreshKey?: number;
}

// App.tsx:26770-26780
const getColSpanClass = (span: number) => {
  switch (span) {
    case 2:
      return "md:col-span-2 lg:col-span-2";
    case 3:
      return "md:col-span-3 lg:col-span-3";
    case 4:
      return "md:col-span-4 lg:col-span-4";
    default:
      return "md:col-span-1 lg:col-span-1";
  }
};

const QUICK_CLASS =
  "bg-white/95 dark:bg-[#12191f]/90 backdrop-blur-2xl p-5 sm:p-6 rounded-3xl flex items-center gap-4 sm:gap-5 transition-all group relative overflow-hidden border border-white/90 dark:border-white/10 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.03),_inset_0_1px_1px_0_rgba(255,255,255,0.9)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.5),_inset_0_1px_1px_0_rgba(255,255,255,0.06)] hover:border-[#f97342]/40 hover:shadow-[0_12px_32px_rgba(249,115,66,0.12)] cursor-pointer";

export function Dashboard(props: DashboardProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const dashboardIconSize = props.iconSize;
  const cardSize = props.cardSize;
  const isEditingDashboard = props.isEditing ?? false;
  const qa = props.quickActions;
  const textAlign = lang === "ar" ? "text-right" : "text-left";
  const unitIqd = t('unit.iqd');

  const icon = (card: CardBase, fallback: ReactNode, className: string) =>
    card.customIcon ? <DynamicIcon name={card.customIcon} size={getLucideIconSize(dashboardIconSize)} className={className} /> : fallback;

  const quickAction = (key: string, onClick: (() => void) | undefined, iconClass: string, iconEl: ReactNode, title: string, sub: string, titleExtra = "") => (
    <motion.button
      key={key}
      variants={itemVariants}
      whileHover={{
        y: -4,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05)",
      }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      data-testid={`quick-${key}`}
      className={QUICK_CLASS}
    >
      <div
        className={`${getIconContainerSizeClass(dashboardIconSize)} ${iconClass} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border`}
      >
        {iconEl}
      </div>
      <div className={`${textAlign} flex-1`}>
        <h4 className={`font-black text-sm dark:text-white uppercase tracking-tight${titleExtra}`}>
          {title}
        </h4>
        <p className="text-[10px] font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest">
          {sub}
        </p>
      </div>
    </motion.button>
  );

  const padding = (s: DashboardCardSize) => (s === "sm" ? "p-5" : s === "lg" ? "p-10" : "p-8");
  const bigValue = (s: DashboardCardSize) => (s === "sm" ? "text-2xl" : s === "lg" ? "text-5xl" : "text-3xl");

  return (
    <motion.div
      key={`dashboard-${props.refreshKey ?? 0}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, scale: 0.95 }}
      className="space-y-8"
      data-testid="dashboard"
    >
      <motion.div
        variants={containerVariants}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6"
      >
        {quickAction("withdraw", qa.onWithdraw, "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200/40 dark:border-amber-500/20", <ArrowDownLeft size={getLucideIconSize(dashboardIconSize)} />, t('dashboard.withdraw'), t('dashboard.withdraw_sub'))}
        {quickAction("deposit", qa.onDeposit, "bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 border-rose-200/40 dark:border-rose-500/20", <ArrowUpRight size={getLucideIconSize(dashboardIconSize)} />, t('deposit'), t('dashboard.deposit_sub'))}
        {quickAction("manual_work", qa.onManualWork, "bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 border-teal-200/40 dark:border-teal-500/20", <Zap size={getLucideIconSize(dashboardIconSize)} />, t('dashboard.manual_work'), t('dashboard.manual_work_sub'))}
        {qa.onTransfersCash && quickAction("transfers_cash_btn", qa.onTransfersCash, "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border-indigo-200/40 dark:border-indigo-500/20", <ArrowUpDown size={getLucideIconSize(dashboardIconSize)} />, t('dashboard.transfers_cash'), t('dashboard.transfers_cash_sub'), " text-indigo-700 dark:text-indigo-400")}
        {qa.onStoreSell && quickAction("store_sell_btn", qa.onStoreSell, "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200/40 dark:border-amber-500/20", <ShoppingBag size={getLucideIconSize(dashboardIconSize)} />, t('dashboard.store_sell'), t('dashboard.store_sell_sub'), " text-amber-600 dark:text-amber-400")}
      </motion.div>
      <motion.div
        variants={containerVariants}
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6`}
      >
        {props.cards.map((card, index) => {
          const size = card.size ?? cardSize;
          const colSpanClass = getColSpanClass(card.colSpan ?? 1);
          const common = { size, dashboardIconSize, isEditing: isEditingDashboard, index, lang, ...(card.onClick ? { onClick: card.onClick } : {}) };
          const lucide = getLucideIconSize(dashboardIconSize);
          switch (card.kind) {
            case 'chart':
              return (
                <motion.div key="combinedTicker" variants={itemVariants} className="md:col-span-2 lg:col-span-3 xl:col-span-4 relative">
                  {card.slot}
                </motion.div>
              );
            case 'totalOwed':
              return (
                <motion.div key="totalOwed" variants={itemVariants} className={colSpanClass} data-testid="card-totalOwed">
                  <StatCard title={t('totalOwed')} value={card.value} {...(card.usdValue ? { usdValue: card.usdValue } : {})} subTitle={t('lastMovement')} subValue={card.lastMovement}
                    icon={icon(card, <ArrowUpRight size={lucide} className="text-rose-600" />, "text-rose-600")} color="bg-rose-50" textColor="text-rose-700" {...common} />
                </motion.div>
              );
            case 'totalCredit':
              return (
                <motion.div key="totalCredit" variants={itemVariants} className={colSpanClass} data-testid="card-totalCredit">
                  <StatCard title={t('totalCredit')} value={card.value} {...(card.usdValue ? { usdValue: card.usdValue } : {})} subTitle={t('lastMovement')} subValue={card.lastMovement}
                    icon={icon(card, <ArrowDownLeft size={lucide} className="text-emerald-600" />, "text-emerald-600")} color="bg-emerald-50" textColor="text-emerald-700" {...common} />
                </motion.div>
              );
            case 'monthlyExpenses':
              return (
                <motion.div key="monthlyExpenses" variants={itemVariants} className={colSpanClass} data-testid="card-monthlyExpenses">
                  <StatCard title={t('monthlyExpenses')} value={card.value} subTitle={t('totalExpenses')} subValue={card.totalExpenses}
                    icon={icon(card, <TrendingDown size={lucide} className="text-rose-600" />, "text-rose-600")} color="bg-rose-50" textColor="text-rose-700" {...common} />
                </motion.div>
              );
            case 'totalProfit':
              // App.tsx:26976-27420 (the editing overlays are cut, see the header)
              return (
                <motion.div key="totalProfit" variants={itemVariants} className={`${getColSpanClass(card.colSpan ?? 2)} relative`} data-testid="card-totalProfit">
                  <div
                    onClick={isEditingDashboard ? undefined : card.onClick}
                    className={`bg-white dark:bg-[#0d1117] ${padding(size)} rounded-3xl border border-gray-100 dark:border-[#30363d] flex flex-col gap-6 transition-all ${isEditingDashboard ? "" : "cursor-pointer hover:border-emerald-500/30"} relative overflow-hidden group shadow-sm`}
                  >
                    <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full -mr-32 -mt-32 blur-3xl transition-opacity group-hover:opacity-100 opacity-0 duration-700" />
                    <div className="flex items-center justify-between relative z-10">
                      <div className="flex items-center gap-4">
                        <div
                          className={`${getIconContainerSizeClass(dashboardIconSize)} bg-emerald-50 dark:bg-emerald-900/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 transition-transform group-hover:scale-110 duration-500`}
                        >
                          <TrendingUp size={lucide} />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 dark:text-[#8b949e] text-[10px] font-bold uppercase tracking-widest mb-1">
                          {t('pendingProfit')}
                        </p>
                        <p
                          className={`${bigValue(size)} font-black text-emerald-600 dark:text-emerald-400 tracking-tight leading-tight flex flex-col items-end`}
                        >
                          <span>
                            {card.pendingProfit}{" "}
                            <span className="text-sm font-bold">
                              {unitIqd}
                            </span>
                          </span>
                          {card.pendingProfitUsd && (
                            <span
                              className="text-sm font-black text-emerald-500 dark:text-emerald-400 flex items-center gap-0.5 mt-1"
                              id="pending-profit-usd"
                            >
                              <span>
                                +{" "}
                                {card.pendingProfitUsd}
                              </span>
                              <DollarSign size={14} className="inline-block" />
                            </span>
                          )}
                          {(card.pendingOther ?? []).map((c, cIdx) => (
                            <span
                              key={`pending-profit-curr-${c.currency || cIdx}`}
                              className="text-xs font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1 mt-1 font-mono"
                            >
                              <span>
                                +{" "}
                                {c.value}
                              </span>
                              <span className="text-[10px] uppercase font-sans bg-emerald-500/10 px-1.5 py-0.5 rounded text-emerald-700 dark:text-emerald-300 font-bold">
                                {c.currency}
                              </span>
                            </span>
                          ))}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6 relative z-10 pt-4 border-t border-gray-50 dark:border-[#30363d]">
                      <div className="space-y-3">
                        <div className="flex flex-col">
                          <span className="text-[9px] text-gray-400 dark:text-[#8b949e] uppercase font-bold mb-1">
                            {t('dailyProfit')}
                          </span>
                          <div className="flex flex-col">
                            <span
                              className="text-xs font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1"
                              title={t('netDailyProfit')}
                              id="net-daily-profit"
                            >
                              <span>
                                {card.netDailyProfit}{" "}
                                {unitIqd}
                              </span>
                              {card.netDailyProfitUsd && (
                                <span
                                  className="text-[10px] text-emerald-500 flex items-center gap-0.5"
                                  id="net-daily-profit-usd"
                                >
                                  <span>
                                    +{" "}
                                    {card.netDailyProfitUsd}
                                  </span>
                                  <DollarSign size={10} />
                                </span>
                              )}
                              {(card.dailyOther ?? []).map((c, cIdx) => (
                                <span
                                  key={`daily-profit-curr-${c.currency || cIdx}`}
                                  className="text-[10px] text-emerald-500 font-mono flex items-center gap-0.5"
                                >
                                  <span>
                                    +{" "}
                                    {c.value}{" "}
                                    {c.currency}
                                  </span>
                                </span>
                              ))}
                            </span>
                            <span
                              className="text-[8px] text-gray-400 font-bold flex items-center gap-1"
                              title={t('grossDailyProfit')}
                            >
                              <span>
                                {card.grossDailyProfit}{" "}
                                {unitIqd}
                              </span>
                              {card.grossDailyProfitUsd && (
                                <span id="gross-daily-profit-usd">
                                  +{" "}
                                  {card.grossDailyProfitUsd}{" "}
                                  $
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] text-gray-400 dark:text-[#8b949e] uppercase font-bold mb-1">
                            {t('capitalizedProfit')}
                          </span>
                          <span
                            className="text-xs font-black text-gray-700 dark:text-[#f0f6fc] flex items-center gap-1"
                            id="capitalized-profit"
                          >
                            <span>
                              {card.capitalizedProfit}{" "}
                              {unitIqd}
                            </span>
                            {card.capitalizedProfitUsd && (
                              <span
                                className="text-[10px] text-emerald-500 flex items-center gap-0.5"
                                id="capitalized-profit-usd"
                              >
                                <span>
                                  +{" "}
                                  {card.capitalizedProfitUsd}
                                </span>
                                <DollarSign size={10} />
                              </span>
                            )}
                          </span>
                          {/* [C26] D-099: the latest profit withdrawal beside the latest capitalization (V-50) */}
                          {(card.lastWithdrawnProfit || card.lastWithdrawnProfitUsd) && (
                            <span
                              className="text-[8px] text-gray-400 font-bold flex items-center gap-1 mt-0.5"
                              id="last-withdrawn-profit"
                              data-testid="last-withdrawn-profit"
                            >
                              <span>
                                {t('dashboard.last_withdrawn_profit')}{" "}
                                {card.lastWithdrawnProfit ?? "0"}{" "}
                                {unitIqd}
                              </span>
                              {card.lastWithdrawnProfitUsd && (
                                <span>
                                  +{" "}
                                  {card.lastWithdrawnProfitUsd}{" "}
                                  $
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-3 text-right">
                        <div className="flex flex-col items-end">
                          <span className="text-[9px] text-gray-400 dark:text-[#8b949e] uppercase font-bold mb-1">
                            {t('profit')}
                          </span>
                          <div className="flex flex-col items-end">
                            <span
                              className="text-xs font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1 justify-end"
                              title={t('netMonthlyProfit')}
                              id="net-monthly-profit"
                            >
                              {(card.monthlyOther ?? []).map((c, cIdx) => (
                                <span
                                  key={`monthly-profit-curr-${c.currency || cIdx}`}
                                  className="text-[10px] text-emerald-500 font-mono flex items-center gap-0.5"
                                >
                                  <span>
                                    +{" "}
                                    {c.value}{" "}
                                    {c.currency}
                                  </span>
                                </span>
                              ))}
                              {card.netMonthlyProfitUsd && (
                                <span
                                  className="text-[10px] text-emerald-500 flex items-center gap-0.5"
                                  id="net-monthly-profit-usd"
                                >
                                  <span>
                                    +{" "}
                                    {card.netMonthlyProfitUsd}
                                  </span>
                                  <DollarSign size={10} />
                                </span>
                              )}
                              <span>
                                {card.netMonthlyProfit}{" "}
                                {unitIqd}
                              </span>
                            </span>
                            <span
                              className="text-[8px] text-gray-400 font-bold flex items-center gap-1 justify-end"
                              title={t('grossMonthlyProfit')}
                            >
                              {card.grossMonthlyProfitUsd && (
                                <span id="gross-monthly-profit-usd">
                                  +{" "}
                                  {card.grossMonthlyProfitUsd}{" "}
                                  ${" "}
                                </span>
                              )}
                              <span>
                                {card.grossMonthlyProfit}{" "}
                                {unitIqd}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-[9px] text-gray-400 dark:text-[#8b949e] uppercase font-bold mb-1">
                            {t('totalProfit')}
                          </span>
                          <span
                            className="text-xs font-black text-gray-700 dark:text-[#f0f6fc] flex items-center gap-1 justify-end"
                            id="total-profit-main"
                          >
                            {card.totalProfitUsd && (
                              <span
                                className="text-[10px] text-emerald-500 flex items-center gap-0.5"
                                id="total-profit-usd"
                              >
                                <span>
                                  +{" "}
                                  {card.totalProfitUsd}
                                </span>
                                <DollarSign size={10} />
                              </span>
                            )}
                            <span>
                              {card.totalProfit}{" "}
                              {unitIqd}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    {!isEditingDashboard && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    )}
                  </div>
                </motion.div>
              );
            case 'inventory':
              return (
                <motion.div key="inventory" variants={itemVariants} className={colSpanClass} data-testid="card-inventory">
                  <StatCard title={t('inventory')} value={card.value}
                    icon={icon(card, <Package size={lucide} className="text-orange-600" />, "text-orange-600")} color="bg-orange-50" textColor="text-orange-700" {...common} />
                </motion.div>
              );
            case 'installments':
              return (
                <motion.div key="installments" variants={itemVariants} className={colSpanClass} data-testid="card-installments">
                  <StatCard title={t('dashboard.installments')} value={card.value} {...(card.usdValue ? { usdValue: card.usdValue } : {})}
                    subTitle={t('dashboard.active_contracts')} subValue={`${card.activeContracts} ${t('dashboard.contracts_unit')}`}
                    icon={icon(card, <CalendarClock size={lucide} className="text-purple-600" />, "text-purple-600")} color="bg-purple-50" textColor="text-purple-700" {...common} />
                </motion.div>
              );
            case 'count':
              return (
                <motion.div key="count" variants={itemVariants} className={`${colSpanClass} relative`} data-testid="card-count">
                  <div
                    onClick={isEditingDashboard ? undefined : card.onClick}
                    className={`bg-white dark:bg-[#0d1117] ${padding(size)} rounded-3xl border border-gray-100 dark:border-[#30363d] flex flex-col gap-6 transition-all ${isEditingDashboard ? "" : "cursor-pointer hover:border-blue-500/30"} relative overflow-hidden group shadow-sm`}
                  >
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full -mr-32 -mt-32 blur-3xl transition-opacity group-hover:opacity-100 opacity-0 duration-700" />
                    <div className="flex items-center justify-between relative z-10">
                      <div
                        className={`${getIconContainerSizeClass(dashboardIconSize)} bg-blue-50 dark:bg-blue-900/10 flex items-center justify-center text-blue-600 dark:text-blue-400 transition-transform group-hover:scale-110 duration-500`}
                      >
                        {card.customIcon ? <DynamicIcon name={card.customIcon} size={lucide} /> : <Activity size={lucide} />}
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 dark:text-[#8b949e] text-[10px] font-bold uppercase tracking-widest mb-1">
                          {t('monthlyCount')}
                        </p>
                        <p
                          className={`${bigValue(size)} font-black text-blue-600 dark:text-blue-400 tracking-tight leading-tight`}
                        >
                          {card.monthly.padStart(3, "0")}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 relative z-10 pt-4 border-t border-gray-50 dark:border-[#30363d]">
                      <div className="space-y-1">
                        <p className="text-[9px] text-gray-400 dark:text-[#8b949e] uppercase font-bold">
                          {t('weeklyCount')}
                        </p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-sm font-black text-gray-700 dark:text-[#f0f6fc]">
                            {card.weekly.padStart(3, "0")}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-1 text-right">
                        <p className="text-[9px] text-gray-400 dark:text-[#8b949e] uppercase font-bold">
                          {t('dailyCount')}
                        </p>
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="text-sm font-black text-gray-700 dark:text-[#f0f6fc]">
                            {card.daily.padStart(3, "0")}
                          </span>
                        </div>
                      </div>
                    </div>
                    {!isEditingDashboard && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    )}
                  </div>
                </motion.div>
              );
            case 'vault':
              return (
                <motion.div key="vault" variants={itemVariants} className={colSpanClass} data-testid="card-vault">
                  <StatCard title={t('vault')} value={card.value}
                    icon={icon(card, <ShieldCheck size={lucide} className="text-indigo-600" />, "text-indigo-600")} color="bg-indigo-50" textColor="text-indigo-700" {...common} />
                </motion.div>
              );
            case 'totalDigital':
              return (
                <motion.div key="totalDigital" variants={itemVariants} className={colSpanClass} data-testid="card-totalDigital">
                  <StatCard title={t('totalDigital')} value={card.value} subTitle={`${t('dashboard.incl_commission')} ${card.inclCommission}`}
                    icon={icon(card, <Wallet size={lucide} className="text-orange-600" />, "text-orange-600")} color="bg-orange-50" textColor="text-orange-700" {...common} />
                </motion.div>
              );
            case 'dollarVault':
              return (
                <motion.div key="dollarVault" variants={itemVariants} className={`${colSpanClass} relative`} data-testid="card-dollarVault">
                  <div
                    onClick={isEditingDashboard ? undefined : card.onClick}
                    className={`bg-white dark:bg-[#0d1117] ${padding(size)} rounded-[2rem] border border-gray-100 dark:border-[#30363d] flex flex-col gap-4 relative overflow-hidden group shadow-sm transition-all ${isEditingDashboard ? "" : "cursor-pointer hover:border-emerald-500/30"}`}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 blur-2xl opacity-100 transition-opacity duration-700" />
                    <div className="flex items-center gap-4 relative z-10">
                      <div
                        className={`${getIconContainerSizeClass(dashboardIconSize)} bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0`}
                      >
                        <DollarSign size={lucide} />
                      </div>
                      <div className="flex-1">
                        <p
                          className={`text-gray-400 dark:text-[#8b949e] ${size === "sm" ? "text-[10px]" : "text-[11px]"} font-bold uppercase`}
                        >
                          {t('dollarVault')}
                        </p>
                        <p
                          className={`${bigValue(size)} font-black text-emerald-600 dark:text-emerald-400 leading-tight font-mono`}
                        >
                          {card.value} $
                        </p>
                      </div>
                    </div>
                    {/* Hover Indicator */}
                    {!isEditingDashboard && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    )}
                  </div>
                </motion.div>
              );
            case 'currenciesVault':
              return (
                <motion.div key="currenciesVault" variants={itemVariants} className={`${colSpanClass} relative`} data-testid="card-currenciesVault">
                  <div
                    onClick={isEditingDashboard ? undefined : card.onClick}
                    className={`bg-white dark:bg-[#0d1117] ${padding(size)} rounded-[2rem] border border-gray-100 dark:border-[#30363d] flex flex-col gap-4 relative overflow-hidden group shadow-sm transition-all ${isEditingDashboard ? "" : "cursor-pointer hover:border-amber-500/30"}`}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -mr-16 -mt-16 blur-2xl opacity-100 transition-opacity duration-700" />
                    <div className="flex items-center gap-4 relative z-10">
                      <div
                        className={`${getIconContainerSizeClass(dashboardIconSize)} bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0`}
                      >
                        <Coins size={lucide} />
                      </div>
                      <div className="flex-1">
                        <p
                          className={`text-gray-400 dark:text-[#8b949e] ${size === "sm" ? "text-[10px]" : "text-[11px]"} font-bold uppercase`}
                        >
                          {t('dashboard.currencies_vault')}
                        </p>
                        <p
                          className={`${bigValue(size)} font-black text-amber-600 dark:text-amber-400 leading-tight font-mono`}
                        >
                          {card.value}{" "}
                          {unitIqd}
                        </p>
                      </div>
                    </div>
                    {/* Hover Indicator */}
                    {!isEditingDashboard && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-amber-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                    )}
                  </div>
                </motion.div>
              );
            case 'capital':
              return (
                <motion.div key="capital_dashboard" variants={itemVariants} className={colSpanClass} data-testid="card-capital">
                  <StatCard title={t('capital')} value={card.value} subTitle={t('initialCapital')} subValue={card.initialCapital}
                    icon={icon(card, <Database size={lucide} className="text-blue-600" />, "text-blue-600")} color="bg-blue-50" textColor="text-blue-700"
                    {...common} />
                </motion.div>
              );
            default:
              return null;
          }
        })}
      </motion.div>
    </motion.div>
  );
}
